import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { APIgetPageCollections } from '../../../../api/Pages';
import { APIgetCollection } from '../../../../api/Collections';

const COLLECTIONS_PER_PAGE = 30;
const liveCollectionsCache = new Map();

export const useLiveCollections = (
  selectedCollections,
  pageId,
  isEditing,
  allowedCollectionUuids = [],
) => {
  const [fetchedCollections, setFetchedCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadedCountRef = useRef(0);
  const selectedRef = useRef([]);

  const selected = Array.isArray(selectedCollections) ? selectedCollections : [];
  const selectedUuids = selected.map((c) => c.uuid).filter(Boolean);
  const uuidsKey = selectedUuids.join(',');
  const normalizedAllowedUuids = useMemo(
    () => Array.from(new Set(
      (Array.isArray(allowedCollectionUuids) ? allowedCollectionUuids : []).filter(Boolean),
    )).sort(),
    [allowedCollectionUuids],
  );
  const allowedSet = useMemo(
    () => new Set(normalizedAllowedUuids),
    [normalizedAllowedUuids],
  );
  const allowedKey = Array.from(allowedSet).sort().join(',');
  const cacheKey = `${uuidsKey}|${pageId || ''}|${isEditing ? 1 : 0}|${allowedKey}`;

  selectedRef.current = selected;

  const mergeOverrides = (liveItem, override) => ({
    ...liveItem,
    localKey: override.localKey,
    title: override.title ?? liveItem.title,
    subtitle: override.subtitle ?? liveItem.subtitle,
    custom_url: override.custom_url ?? liveItem.custom_url,
  });

  const fetchBatch = useCallback((uuidsBatch, append) => {
    if (uuidsBatch.length === 0) {
      if (!append) {
        setFetchedCollections([]);
      }
      setLoading(false);
      setLoadingMore(false);
      setHasMore(false);
      return;
    }

    if (append) setLoadingMore(true);
    else setLoading(true);

    const currentSelectedUuids = selectedRef.current.map((c) => c.uuid).filter(Boolean);

    const request = isEditing
      ? (() => {
        const allowedBatchUuids = uuidsBatch.filter((uuid) => allowedSet.has(uuid));
        const regularBatchUuids = uuidsBatch.filter((uuid) => !allowedSet.has(uuid));

        const allowedPromise = allowedBatchUuids.length > 0
          ? APIgetPageCollections(pageId, allowedBatchUuids)
            .then((res) => res.data?.data ?? res.data ?? [])
            .catch(() => [])
          : Promise.resolve([]);

        const regularPromise = regularBatchUuids.length > 0
          ? Promise.all(
            regularBatchUuids.map((uuid) => APIgetCollection(uuid)
              .then((res) => res.data)
              .catch(() => null)),
          ).then((results) => results.filter(Boolean))
          : Promise.resolve([]);

        return Promise.all([allowedPromise, regularPromise]).then(
          ([allowedItems, regularItems]) => {
            const byUuid = new Map([
              ...allowedItems.map((item) => [item?.uuid, item]),
              ...regularItems.map((item) => [item?.uuid, item]),
            ]);

            return uuidsBatch
              .map((uuid) => byUuid.get(uuid))
              .filter(Boolean);
          },
        );
      })()
      : APIgetPageCollections(pageId, uuidsBatch)
        .then((res) => res.data?.data ?? res.data ?? [])
        .catch(() => []);

    request
      .then((items) => {
        let nextCollections = items;
        if (append) {
          setFetchedCollections((prev) => {
            nextCollections = [...prev, ...items];
            return nextCollections;
          });
        } else {
          setFetchedCollections(items);
        }

        const nextLoadedCount = loadedCountRef.current + uuidsBatch.length;
        loadedCountRef.current = nextLoadedCount;
        const nextHasMore = nextLoadedCount < currentSelectedUuids.length;
        setHasMore(nextHasMore);

        liveCollectionsCache.set(cacheKey, {
          collections: nextCollections,
          loadedCount: nextLoadedCount,
          hasMore: nextHasMore,
        });
      })
      .catch(() => {
        if (!append) setFetchedCollections([]);
        setHasMore(false);
        liveCollectionsCache.delete(cacheKey);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [cacheKey, pageId, isEditing, allowedSet]);

  useEffect(() => {
    loadedCountRef.current = 0;

    if (selectedUuids.length === 0) {
      setFetchedCollections([]);
      setLoading(false);
      setHasMore(false);
      return;
    }

    const cachedCollections = liveCollectionsCache.get(cacheKey);
    if (cachedCollections) {
      setFetchedCollections(cachedCollections.collections || []);
      loadedCountRef.current = cachedCollections.loadedCount || 0;
      setHasMore(!!cachedCollections.hasMore);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const firstBatch = selectedUuids.slice(0, COLLECTIONS_PER_PAGE);
    fetchBatch(firstBatch, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, uuidsKey, pageId, isEditing]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;

    const currentSelectedUuids = selectedRef.current.map((c) => c.uuid).filter(Boolean);
    const nextBatch = currentSelectedUuids.slice(
      loadedCountRef.current,
      loadedCountRef.current + COLLECTIONS_PER_PAGE,
    );

    fetchBatch(nextBatch, true);
  }, [loadingMore, hasMore, fetchBatch]);

  const liveCollections = (() => {
    if (!Array.isArray(fetchedCollections)) return fetchedCollections;
    const fetchedByUuid = new Map(fetchedCollections.map((item) => [item.uuid, item]));

    return selected
      .filter((override) => fetchedByUuid.has(override.uuid))
      .map((override) => mergeOverrides(fetchedByUuid.get(override.uuid), override));
  })();

  return {
    liveCollections,
    loading,
    loadingMore,
    hasMore,
    loadMore,
  };
};
